import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { wrappedIndex } from "@/lib/wrapped-index";

/**
 * The two underline tabs the design draws, one module so they agree on how a
 * tab sits, lights on hover and shows that it is open.
 *
 * `strip` is a stage's steps across the top of its screens (`.figtab`): a
 * tinted ground under the open one and a thick rule. `page` is a page's own
 * lists (`.hmtab`): larger words, and a thin rule inset under the open one.
 */
const LOOK = {
  strip: {
    tab: "-mb-[2px] rounded-t-[8px] border-b-[3px] border-transparent px-[18px] pb-[12px] pt-[11px] text-[14px] font-bold",
    on: "border-primary bg-muted text-primary",
  },
  page: {
    tab: "relative rounded-t-[10px] px-[13px] pb-[11px] pt-[9px] text-[16px] font-semibold",
    on: "text-primary after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-brand-start",
  },
} as const;

/** How one tab is drawn: open or not, and closed when it leads nowhere yet. */
export function tabChrome(
  look: keyof typeof LOOK,
  { active, disabled = false }: { active: boolean; disabled?: boolean },
) {
  return cn(
    "flex cursor-pointer items-center gap-2 whitespace-nowrap bg-transparent text-muted-foreground transition-colors duration-200",
    LOOK[look].tab,
    active && LOOK[look].on,
    disabled
      ? "cursor-not-allowed opacity-45"
      : "hover:bg-muted hover:text-primary",
  );
}

const COUNT =
  "rounded-full border border-border bg-surface-sunken px-[7px] py-px text-[13px] font-bold text-muted-foreground";
const COUNT_ON = "border-primary/25 bg-muted text-primary";

/** The keys that move along a tab list, and where each one lands. */
function nextIndex(key: string, at: number, length: number): number | null {
  switch (key) {
    case "ArrowRight":
      return wrappedIndex(at, 1, length);
    case "ArrowLeft":
      return wrappedIndex(at, -1, length);
    case "Home":
      return 0;
    case "End":
      return length - 1;
    default:
      return null;
  }
}

/**
 * A page's lists as underline tabs, each with its count, over the open list.
 *
 * A tab list in the ARIA sense: one stop in the tab order, the arrows, Home
 * and End move along it and open the tab they land on, and the panel is named
 * by its tab. Which tab is open is the caller's, so a tab that empties can be
 * left for another.
 */
export function Tabs<Id extends string>({
  label,
  tabs,
  selected,
  onSelect,
  children,
}: {
  label: string;
  tabs: readonly { id: Id; label: string; count?: number }[];
  selected: Id;
  onSelect: (id: Id) => void;
  children?: ReactNode;
}) {
  const id = useId();
  const tabsRef = useRef(new Map<Id, HTMLButtonElement>());
  const onKeyDown = (event: KeyboardEvent, at: number) => {
    const next = nextIndex(event.key, at, tabs.length);
    if (next === null) return;
    event.preventDefault();
    const tab = tabs[next];
    onSelect(tab.id);
    tabsRef.current.get(tab.id)?.focus();
  };
  return (
    <div>
      <div
        role="tablist"
        aria-label={label}
        className="mb-5 flex flex-wrap gap-1 border-b border-border"
      >
        {tabs.map((tab, at) => {
          const active = tab.id === selected;
          return (
            <button
              key={tab.id}
              ref={(button) => {
                if (button === null) tabsRef.current.delete(tab.id);
                else tabsRef.current.set(tab.id, button);
              }}
              type="button"
              role="tab"
              id={`${id}-${tab.id}`}
              aria-selected={active}
              aria-controls={`${id}-panel`}
              tabIndex={active ? 0 : -1}
              onClick={() => {
                onSelect(tab.id);
              }}
              onKeyDown={(event) => {
                onKeyDown(event, at);
              }}
              className={tabChrome("page", { active })}
            >
              {tab.label}{" "}
              {tab.count !== undefined && (
                <span className={cn(COUNT, active && COUNT_ON)}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={`${id}-panel`}
        aria-labelledby={`${id}-${selected}`}
      >
        {children}
      </div>
    </div>
  );
}
